/**
 * Loans: the owner's ledger, and proposing or recording one.
 */
import type {
  BorrowedLoan,
  BorrowedLoansResponse,
  Loan,
  LoansResponse,
  ProposeLoanRequest,
  SetLocationRequest,
  WorkLocation,
} from "@ondo/shared";

import { request, requestWith } from "./request.ts";

/** The owner's loan ledger, live and finished alike. */
export const fetchOwnerLoans = () =>
  request<LoansResponse>("/loans/mine").then((body) => body.loans);

/**
 * Propose a loan — or, with no borrower named, simply record one. Which of the
 * two happened is read off the returned status, and the form says so.
 */
export const proposeLoan = (workId: string, body: ProposeLoanRequest) =>
  request<Loan>(`/works/${workId}/loans`, body satisfies ProposeLoanRequest);

/**
 * Where the owner keeps a work. PUT, because it replaces: the last location
 * ends and this one begins, and sending the same one twice changes nothing.
 */
export const setWorkLocation = (workId: string, body: SetLocationRequest) =>
  requestWith<WorkLocation>("PUT", `/works/${workId}/location`, body satisfies SetLocationRequest);

/** The lender ends a loan before it goes out; the borrower's grant goes with it. */
export const cancelLoan = (loanId: string) =>
  request<void>(`/loans/${loanId}/cancel`, {});

/**
 * The BORROWER's ledger: loans made to this account, live and finished alike.
 *
 * A different route and a different shape from `fetchOwnerLoans`, not a filter
 * on it. The two reads have different bounds in SQL and name different
 * counterparties -- this one names the LENDER, because that is who the borrower
 * needs to see.
 */
export const fetchBorrowedLoans = () =>
  request<BorrowedLoansResponse>("/loans/borrowed").then((body) => body.loans);

/** The borrower assents. Their read of the work becomes a grant with it. */
export const acceptLoan = (loanId: string) =>
  request<BorrowedLoan>(`/loans/${loanId}/accept`, {});

/**
 * The borrower refuses.
 *
 * NOT `cancelLoan` with a different caller: that writes 'cancelled', which is
 * the lender unsaying their own proposal. This writes 'declined', so the record
 * never names the lender as the author of a refusal they did not make.
 */
export const declineLoan = (loanId: string) =>
  request<BorrowedLoan>(`/loans/${loanId}/decline`, {});

/** The lender sends it. The one act on a loan that is neither party's answer. */
export const dispatchLoan = (loanId: string) =>
  request<Loan>(`/loans/${loanId}/dispatch`, {});

/**
 * The borrower says the object arrived.
 *
 * Records a timestamp and leaves the status alone -- a crate in transit and a
 * picture on the wall are both 'out'. Nothing but this call can fill it in:
 * there is no timer anywhere that marks a loan received.
 */
export const markLoanReceived = (loanId: string) =>
  request<BorrowedLoan>(`/loans/${loanId}/received`, {});

/**
 * The work comes home. EITHER party may say so.
 *
 * Returns nothing, because the caller may be either one and the server will not
 * guess which ledger to hand back. The screen re-reads its own.
 */
export const markLoanReturned = (loanId: string) =>
  request<void>(`/loans/${loanId}/returned`, {});
